import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../../ui/dropdown-menu';
import {
  CalendarDays,
  Search,
  Eye,
  Edit,
  Clock,
  CheckCircle,
  Calendar,
  Plus,
  AlertTriangle,
  MoreVertical,
  Trash2,
  Archive,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { scheduleService } from '../../../src/services/schedule.service';
import { useAuth } from '../../../src/hooks/useAuth';
import { useSchedules, useAllScheduleTasks } from '../../../src/hooks/queries/use-schedules';
import { useProjects } from '../../../src/hooks/queries/use-projects';
import { useOrgUsers } from '../../../src/hooks/queries/use-org-users';
import { TaskDetailsModal, TaskStatusUpdateModal, NewTaskModal, getPriorityBadge } from './pm_tasks';
import { NewScheduleModal, ViewScheduleModal, EditScheduleModal } from './pm_schedule_modal';
import { showError, showSuccess, showDeleteConfirm } from '../../../src/utils/notification.utils';

const formatDate = (dateStr: string) => {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const getDaysDifference = (startDate: string, endDate: string) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = end.getTime() - start.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

export function SchedulesAndTasks() {
  const [activeTab, setActiveTab] = useState("timeline");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [ownerFilter, setOwnerFilter] = useState("all");
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [viewingSchedule, setViewingSchedule] = useState<any>(null);
  const [editingSchedule, setEditingSchedule] = useState<any>(null);

  const { orgId } = useAuth();

  const { data: schedules = [], isLoading: schedulesLoading, refetch: refetchSchedules } = useSchedules(orgId);
  const scheduleIds = schedules.map((s: any) => s.schedule_id);
  const { data: tasks = [], isLoading: tasksLoading, refetch: refetchTasks } = useAllScheduleTasks(scheduleIds);
  const { data: projects = [] } = useProjects(orgId);
  const { data: users = [] } = useOrgUsers(orgId);

  const loading = schedulesLoading || (scheduleIds.length > 0 && tasksLoading);

  const filteredTasks = tasks.filter((task: any) => {
    const matchesSearch = (task.task_name?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
                          (task.schedule_name?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
                          (task.description?.toLowerCase() || '').includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || task.status?.toLowerCase() === statusFilter;
    const matchesOwner = ownerFilter === 'all' || task.assigned_to_user_id === ownerFilter;
    return matchesSearch && matchesStatus && matchesOwner;
  });

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    return { daysInMonth, startingDayOfWeek, firstDay, lastDay };
  };

  const getTasksForDate = (date: Date) => {
    return tasks.filter((task: any) => {
      if (!task.start_date_planned || !task.end_date_planned) return false;
      const taskStart = new Date(task.start_date_planned);
      const taskEnd = new Date(task.end_date_planned);
      const currentDate = new Date(date);
      currentDate.setHours(0, 0, 0, 0);
      taskStart.setHours(0, 0, 0, 0);
      taskEnd.setHours(0, 0, 0, 0);
      return currentDate >= taskStart && currentDate <= taskEnd;
    });
  };

  const goToPreviousMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  const goToNextMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  const goToToday = () => setCurrentMonth(new Date());

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Schedules & Tasks</h2>
          <p className="text-muted-foreground">Project schedules, milestones, and task management</p>
        </div>
        <div className="flex gap-2">
          {activeTab === 'timeline' && <NewScheduleModal onSuccess={() => refetchSchedules()} />}
          {activeTab === 'tasks' && <NewTaskModal onSuccess={() => refetchTasks()} />}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4 text-blue-600" />
              Active Tasks
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-700">{tasks.filter((t: any) => t.status?.toLowerCase() === 'in progress').length}</div>
            <p className="text-xs text-blue-600 mt-1">Currently in progress</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              Completed Tasks
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-700">{tasks.filter((t: any) => t.status?.toLowerCase() === 'completed').length}</div>
            <p className="text-xs text-green-600 mt-1">Tasks finished</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              Overdue Tasks
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-700">
              {tasks.filter((t: any) => {
                if (!t.end_date_planned) return false;
                const endDate = new Date(t.end_date_planned);
                const today = new Date();
                return t.status?.toLowerCase() !== 'completed' && endDate < today;
              }).length}
            </div>
            <p className="text-xs text-red-600 mt-1">Need immediate attention</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <CalendarDays className="h-4 w-4 text-purple-600" />
              Upcoming Milestones
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-700">{schedules.filter((s: any) => s.status?.toLowerCase() === 'active').length}</div>
            <p className="text-xs text-purple-600 mt-1">Active schedules</p>
          </CardContent>
        </Card>
      </div>

      {/* Controlled modals rendered outside dropdown */}
      {viewingSchedule && (
        <ViewScheduleModal
          schedule={viewingSchedule}
          projects={projects}
          open={true}
          onOpenChange={(o) => { if (!o) setViewingSchedule(null); }}
        />
      )}
      {editingSchedule && (
        <EditScheduleModal
          schedule={editingSchedule}
          projects={projects}
          onSuccess={() => { refetchSchedules(); setEditingSchedule(null); }}
          open={true}
          onOpenChange={(o) => { if (!o) setEditingSchedule(null); }}
        />
      )}

      {/* Main Tabs Content */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5" />
            <h3 className="text-lg font-semibold">Schedule Management</h3>
          </div>
          <p className="text-sm text-muted-foreground">Manage project timelines, milestones, and tasks</p>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="timeline">Project Timeline</TabsTrigger>
              <TabsTrigger value="tasks">Task List</TabsTrigger>
              <TabsTrigger value="calendar">Calendar View</TabsTrigger>
            </TabsList>

            {/* Timeline Tab */}
            <TabsContent value="timeline" className="space-y-4">
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Project Schedules</h3>
                {loading ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-muted-foreground">Loading schedules...</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {schedules.map((schedule: any) => {
                      const projectName = projects.find((p: any) => p.project_id === schedule.project_id)?.project_name || schedule.project_id || 'Unknown';

                      return (
                        <div key={schedule.schedule_id} className="flex items-center justify-between p-4 border rounded-lg">
                          <div className="flex items-center gap-4">
                            <div className={`w-3 h-3 rounded-full ${
                              schedule.status?.toLowerCase() === 'completed' ? 'bg-green-600' :
                              schedule.status?.toLowerCase() === 'active' ? 'bg-blue-600' :
                              schedule.status?.toLowerCase() === 'baseline' ? 'bg-purple-600' :
                              'bg-gray-300'
                            }`} />
                            <div>
                              <h4 className="font-medium">{schedule.schedule_name}</h4>
                              <p className="text-sm text-muted-foreground">
                                {projectName} • Version: {schedule.version}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-muted-foreground">
                              {formatDate(schedule.start_date)} - {formatDate(schedule.end_date)}
                            </span>
                            <Badge variant={
                              schedule.status?.toLowerCase() === 'active' ? 'default' :
                              schedule.status?.toLowerCase() === 'completed' ? 'default' :
                              'outline'
                            } className={
                              schedule.status?.toLowerCase() === 'completed' ? 'bg-green-600' :
                              schedule.status?.toLowerCase() === 'active' ? 'bg-blue-600' : ''
                            }>
                              {schedule.status}
                            </Badge>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => setViewingSchedule(schedule)}>
                                  <Eye className="h-4 w-4 mr-2" />
                                  View Details
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setEditingSchedule(schedule)}>
                                  <Edit className="h-4 w-4 mr-2" />
                                  Edit Schedule
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-orange-600"
                                  onClick={async () => {
                                    try {
                                      await scheduleService.updateSchedule(schedule.schedule_id, { status: 'archived' } as any);
                                      showSuccess({ title: 'Schedule Archived', message: 'Schedule has been archived successfully' });
                                      refetchSchedules();
                                    } catch (error) {
                                      showError({ title: 'Archive Failed', message: 'Failed to archive schedule' });
                                    }
                                  }}
                                >
                                  <Archive className="h-4 w-4 mr-2" />
                                  Archive
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  className="text-red-600"
                                  onClick={async () => {
                                    const confirmed = await showDeleteConfirm(schedule.schedule_name, 'schedule');
                                    if (confirmed) {
                                      try {
                                        await scheduleService.deleteSchedule(schedule.schedule_id);
                                        showSuccess({ title: 'Schedule Deleted', message: 'Schedule has been deleted successfully' });
                                        refetchSchedules();
                                      } catch (error) {
                                        showError({ title: 'Delete Failed', message: 'Failed to delete schedule' });
                                      }
                                    }
                                  }}
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                      );
                    })}

                    {schedules.length === 0 && !loading && (
                      <div className="text-center py-12">
                        <CalendarDays className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                        <h3 className="text-lg font-medium mb-2">No schedules found</h3>
                        <p className="text-muted-foreground">Create your first schedule to get started.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Tasks Tab */}
            <TabsContent value="tasks" className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search tasks, projects, or descriptions..."
                    className="pl-9"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="not started">Not Started</SelectItem>
                    <SelectItem value="in progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="on hold">On Hold</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={ownerFilter} onValueChange={setOwnerFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Owners</SelectItem>
                    {users.map((user: any) => (
                      <SelectItem key={user.user_id} value={user.user_id}>
                        {user.username || `${user.first_name} ${user.last_name}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-muted-foreground">Loading tasks...</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredTasks.map((task: any) => {
                    const owner = users.find((u: any) => u.user_id === task.assigned_to_user_id);
                    const ownerName = owner
                      ? `${owner.first_name || ''} ${owner.last_name || ''}`.trim() || owner.username || 'Unassigned'
                      : 'Unassigned';

                    return (
                      <div key={task.task_id} className="p-4 border rounded-lg">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h4 className="font-medium">{task.task_name}</h4>
                              <Badge variant={
                                task.status?.toLowerCase() === 'in progress' ? 'default' :
                                task.status?.toLowerCase() === 'completed' ? 'default' :
                                'secondary'
                              } className={
                                task.status?.toLowerCase() === 'completed' ? 'bg-green-600' :
                                task.status?.toLowerCase() === 'in progress' ? 'bg-blue-600' : ''
                              }>
                                {task.status}
                              </Badge>
                              {task.priority && getPriorityBadge(task.priority)}
                            </div>
                            {task.description && (
                              <p className="text-sm text-muted-foreground mb-2">{task.description}</p>
                            )}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              <div>
                                <span className="font-medium">Schedule:</span>
                                <p className="text-muted-foreground">{task.schedule_name || 'N/A'}</p>
                              </div>
                              <div>
                                <span className="font-medium">Owner:</span>
                                <p className="text-muted-foreground">{ownerName}</p>
                              </div>
                              <div>
                                <span className="font-medium">Timeline:</span>
                                <p className="text-muted-foreground">{formatDate(task.start_date_planned)} to {formatDate(task.end_date_planned)}</p>
                              </div>
                              <div>
                                <span className="font-medium">Duration:</span>
                                <p className="text-muted-foreground">{task.duration_days_planned || getDaysDifference(task.start_date_planned, task.end_date_planned)} days</p>
                              </div>
                            </div>
                            {task.status?.toLowerCase() === 'in progress' && task.progress_percentage && (
                              <div className="mt-3">
                                <div className="flex items-center justify-between text-sm mb-1">
                                  <span>Progress</span>
                                  <span>{task.progress_percentage}%</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                  <div
                                    className="bg-blue-600 h-2 rounded-full"
                                    style={{ width: `${task.progress_percentage}%` }}
                                  ></div>
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            <TaskDetailsModal
                              mode="view"
                              task={task}
                              trigger={
                                <Button variant="ghost" size="sm" title="View Task">
                                  <Eye className="h-4 w-4" />
                                </Button>
                              }
                            />
                            <TaskDetailsModal
                              mode="edit"
                              task={task}
                              onSuccess={() => refetchTasks()}
                              trigger={
                                <Button variant="ghost" size="sm" title="Edit Task">
                                  <Edit className="h-4 w-4" />
                                </Button>
                              }
                            />
                            {task.status?.toLowerCase() === 'not started' && (
                              <TaskStatusUpdateModal
                                task={task}
                                action="start"
                                onSuccess={() => refetchTasks()}
                                trigger={
                                  <Button variant="ghost" size="sm" title="Start Task" className="text-blue-600 hover:text-blue-700">
                                    <Clock className="h-4 w-4" />
                                  </Button>
                                }
                              />
                            )}
                            {task.status?.toLowerCase() === 'in progress' && (
                              <TaskStatusUpdateModal
                                task={task}
                                action="complete"
                                onSuccess={() => refetchTasks()}
                                trigger={
                                  <Button variant="ghost" size="sm" title="Complete Task" className="text-green-600 hover:text-green-700">
                                    <CheckCircle className="h-4 w-4" />
                                  </Button>
                                }
                              />
                            )}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  className="text-orange-600"
                                  onClick={async () => {
                                    try {
                                      await scheduleService.updateTask(task.schedule_id, task.task_id, { status: 'Archived' });
                                      showSuccess({ title: 'Task Archived', message: 'Task has been archived successfully' });
                                      refetchTasks();
                                    } catch (error) {
                                      showError({ title: 'Archive Failed', message: 'Failed to archive task' });
                                    }
                                  }}
                                >
                                  <Archive className="h-4 w-4 mr-2" />
                                  Archive Task
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  className="text-red-600"
                                  onClick={async () => {
                                    const confirmed = await showDeleteConfirm(task.task_name, 'task');
                                    if (confirmed) {
                                      try {
                                        await scheduleService.updateTask(task.schedule_id, task.task_id, { status: 'Deleted' });
                                        showSuccess({ title: 'Task Deleted', message: 'Task has been deleted successfully' });
                                        refetchTasks();
                                      } catch (error) {
                                        showError({ title: 'Delete Failed', message: 'Failed to delete task' });
                                      }
                                    }
                                  }}
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete Task
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {filteredTasks.length === 0 && !loading && (
                    <div className="text-center py-12">
                      <CalendarDays className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                      <h3 className="text-lg font-medium mb-2">No tasks found</h3>
                      <p className="text-muted-foreground">
                        {searchQuery || statusFilter !== "all" || ownerFilter !== "all"
                          ? "Try adjusting your search criteria or filters"
                          : "No tasks available. Create your first task to get started."
                        }
                      </p>
                    </div>
                  )}
                </div>
              )}
            </TabsContent>

            {/* Calendar Tab */}
            <TabsContent value="calendar" className="space-y-4">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={goToPreviousMonth}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <h3 className="text-lg font-semibold min-w-[200px] text-center">
                      {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </h3>
                    <Button variant="outline" size="sm" onClick={goToNextMonth}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                  <Button variant="outline" size="sm" onClick={goToToday}>
                    Today
                  </Button>
                </div>

                <div className="border rounded-lg overflow-hidden">
                  <div className="grid grid-cols-7 bg-muted">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                      <div key={day} className="p-2 text-center text-sm font-medium border-r last:border-r-0">
                        {day}
                      </div>
                    ))}
                  </div>

                  <div className="grid grid-cols-7">
                    {(() => {
                      const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentMonth);
                      const days = [];
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);

                      for (let i = 0; i < startingDayOfWeek; i++) {
                        days.push(
                          <div key={`empty-${i}`} className="min-h-[100px] p-2 bg-muted/30 border-r border-b"></div>
                        );
                      }

                      for (let day = 1; day <= daysInMonth; day++) {
                        const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
                        date.setHours(0, 0, 0, 0);
                        const tasksForDay = getTasksForDate(date);
                        const isToday = date.getTime() === today.getTime();

                        days.push(
                          <div
                            key={day}
                            className={`min-h-[100px] p-2 border-r border-b last:border-r-0 ${isToday ? 'bg-blue-50' : ''}`}
                          >
                            <div className={`text-sm font-medium mb-1 ${isToday ? 'text-blue-600' : ''}`}>
                              {day}
                            </div>
                            <div className="space-y-1">
                              {tasksForDay.slice(0, 3).map((task: any) => (
                                <div
                                  key={task.task_id}
                                  className="text-xs p-1 rounded truncate cursor-pointer hover:opacity-80"
                                  style={{
                                    backgroundColor: task.status?.toLowerCase() === 'completed' ? '#dcfce7' :
                                                   task.status?.toLowerCase() === 'in progress' ? '#dbeafe' :
                                                   '#f3f4f6',
                                    color: task.status?.toLowerCase() === 'completed' ? '#166534' :
                                          task.status?.toLowerCase() === 'in progress' ? '#1e40af' :
                                          '#374151'
                                  }}
                                  title={task.task_name}
                                >
                                  {task.task_name}
                                </div>
                              ))}
                              {tasksForDay.length > 3 && (
                                <div className="text-xs text-muted-foreground">
                                  +{tasksForDay.length - 3} more
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      }

                      return days;
                    })()}
                  </div>
                </div>

                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded" style={{ backgroundColor: '#dbeafe' }}></div>
                    <span>In Progress</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded" style={{ backgroundColor: '#dcfce7' }}></div>
                    <span>Completed</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded" style={{ backgroundColor: '#f3f4f6' }}></div>
                    <span>Not Started</span>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
